import { DrawerLink } from '../../app/DrawerLink';

import { Card } from '../../components/ui/Card';
import { usePlayer } from '../../stores/player';
import { Artwork } from './Artwork';
import { duration, type Dashboard, type DashboardTrack, type Reach } from './api';
import { Verified } from '../../components/ui/Verified';

/**
 * "Because you saved …"
 *
 * ## The claim this panel makes, and the one it does not
 *
 * The design mockup puts a match percentage next to each row - 92%, 88%, 81%.
 * There is no similarity model here that could produce one. Inventing it would
 * be the single most corrosive thing on the page: once a reader works out that
 * one number is decorative, the play counts and follower counts beside it stop
 * meaning anything either.
 *
 * So the panel says what the query actually did. `basis` comes from the server -
 * `genre` when the saved track had one and these share it, `popular` when it did
 * not - and the subheading is written from that rather than asserted in the
 * template. When there is a real recommender, the panel gets a real score.
 */
export function BecauseYouSaved({ recommended }: { recommended: NonNullable<Dashboard['recommended']> }) {
  // The whole panel is the queue, so playing the third row continues into
  // nothing rather than stopping dead after one track.
  const queue = recommended.items.filter((track) => track.status === 'ready');

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-col gap-1">
        <h2 className="meta-caps text-faint">Because you saved</h2>
        <DrawerLink
          to={`/tracks/${recommended.seed.id}`}
          className="text-lg font-semibold tracking-tight text-ink hover:underline"
        >
          {recommended.seed.title}
        </DrawerLink>
        <p className="text-xs text-faint">
          {recommended.basis === 'genre' && recommended.seed.genre
            ? `More ${recommended.seed.genre.toLowerCase()}, most played first`
            : 'What else people are playing'}
        </p>
      </div>

      <ul className="flex flex-col gap-1" data-recommended>
        {recommended.items.map((track) => (
          <li key={track.id}>
            <RecommendedRow track={track} queue={queue} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function RecommendedRow({ track, queue }: { track: DashboardTrack; queue: DashboardTrack[] }) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const toggle = usePlayer((state) => state.toggle);
  const isCurrent = current?.id === track.id;

  return (
    <button
      type="button"
      aria-label={`Play ${track.title}`}
      onClick={() => (isCurrent ? toggle() : void play(track, queue, 'dashboard'))}
      className="flex w-full items-center gap-3 rounded-[var(--radius-control)] p-2 text-left transition-colors hover:bg-raised"
      data-recommended-track={track.id}
    >
      <Artwork track={track} className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)]" />

      <span className="flex min-w-0 flex-1 flex-col">
        <span className={`truncate text-sm ${isCurrent ? 'text-accent' : 'text-ink'}`}>
          {track.title}
        </span>
        <span className="truncate font-mono text-[11px] text-faint">
          {track.owner.display_name}
          {track.owner?.verified && <Verified />}
        </span>
      </span>

      <span className="shrink-0 font-mono text-[11px] text-faint">{duration(track.duration_ms)}</span>
    </button>
  );
}

/**
 * "Your reach."
 *
 * ## Why the headline is a week
 *
 * A lifetime play count only ever rises, which makes it useless as a signal - it
 * reads the same on the week a track gets picked up as on the week nobody
 * listens. Seven days is the figure that can fall, and a number that can fall is
 * the only kind worth checking twice.
 *
 * The lifetime totals are still here, as the quiet line underneath, because the
 * artist looking at this wants both.
 *
 * ## And why listeners sit next to plays
 *
 * `listeners_7d` counts distinct `listener_key`, so somebody who played a track
 * four times is one person. Forty plays from two people and forty from forty is
 * the difference between a friend and an audience, and a play count alone cannot
 * tell them apart.
 *
 * The panel only renders for somebody who has released something - see
 * `ShowDashboardAction` for why zeroes are omitted rather than shown.
 */
export function YourReach({ reach }: { reach: Reach }) {
  return (
    <Card tone="fresh" className="flex flex-col gap-3 p-5">
      <h2 className="meta-caps text-fresh">Your reach</h2>

      <p className="text-4xl font-semibold tracking-tight text-ink">
        {reach.plays_7d.toLocaleString()}
      </p>

      <p className="text-sm leading-relaxed text-soft">
        {reach.plays_7d === 0 ? (
          <>No plays in the last seven days.</>
        ) : (
          <>
            {reach.plays_7d === 1 ? 'play' : 'plays'} on your tracks this week, from{' '}
            {reach.listeners_7d.toLocaleString()}{' '}
            {reach.listeners_7d === 1 ? 'listener' : 'listeners'}.
          </>
        )}
      </p>

      <dl className="mt-1 grid grid-cols-3 gap-3 border-t border-fresh-edge pt-3">
        <Stat label="Plays" value={reach.plays} hint="all time" />
        <Stat label="Saves" value={reach.saves} hint="all time" />
        <Stat label="Followers" value={reach.followers} />
      </dl>
    </Card>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="meta-caps text-faint">{label}</dt>
      <dd className="font-mono text-sm text-ink">{value.toLocaleString()}</dd>
      {hint && <span className="text-[10px] text-ghost">{hint}</span>}
    </div>
  );
}
